import React, { useEffect, useState } from "react";
import { Button, Typography, Space, Popconfirm } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import UploadDatasetModal from "./components/UploadDatasetModal";
import GenericTable from "../GeneralTable/GenericTable";
import "./DataCleaningPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGate } from "../Subscription/entitlements";
import {
  getDatasets,
  uploadInternalDataset,
  deleteDataset,
  exportDataset,
} from "./services/dataCleaning.service";

const { Title, Text } = Typography;

const DataCleaningPage: React.FC = () => {
  const { t } = useTranslation("dataCleaning");
  const gate = useGate();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [loading, setLoading] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const fetchRows = async (currentPage = page) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await getDatasets(projectId, currentPage, pageSize);
      // API returns { total, page, page_size, items: [{id, name}] }
      setTotal(data.total ?? 0);
      setRows(
        (data.items ?? []).map((item: { id: string; name: string }) => ({
          key: item.id,
          id: item.id,
          name: item.name,
        })),
      );
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(1);
    setPage(1);
  }, [projectId]);

  const handleOpenUpload = () => {
    setUploadError("");
    setSelectedFile(null);
    setIsUploadOpen(true);
  };

  const handleSubmitUpload = async () => {
    if (!selectedFile) {
      setUploadError(t("errors.fileRequired"));
      return;
    }
    if (!projectId) {
      setUploadError(t("errors.projectIdMissing"));
      return;
    }
    const lowerName = selectedFile.name.toLowerCase();
    const isValid = [".xlsx", ".xls", ".csv"].some((ext) =>
      lowerName.endsWith(ext),
    );
    if (!isValid) {
      setUploadError(t("errors.unexpectedFileType"));
      return;
    }
    setUploading(true);
    try {
      await uploadInternalDataset({ file: selectedFile, projectId });
      setUploadError("");
      setIsUploadOpen(false);
      setSelectedFile(null);
      await fetchRows(1);
      setPage(1);
    } catch {
      setUploadError(t("errors.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (datasetId: string) => {
    try {
      await deleteDataset(datasetId);
      await fetchRows(page);
    } catch {}
  };

  const handleDownload = async (datasetId: string, name: string) => {
    try {
      const blob = await exportDataset(datasetId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name || "dataset"}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const handleView = (record: any) => {
    navigate(`/data-cleaning/${projectId}/review/${record.id}`);
  };

  const columns: ColumnsType<any> = [
    {
      title: t("table.datasetName"),
      dataIndex: "name",
      key: "name",
      render: (value: string, record: any) => (
        <span
          className="dc-list-dataset-name"
          onClick={() => handleView(record)}
        >
          {value || "-"}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 130,
      align: "right" as const,
      render: (_: any, record: any) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button
            type="text"
            icon={<EditOutlined style={{ color: "#8c6cff" }} />}
            onClick={() => handleView(record)}
          />
          <Button
            type="text"
            icon={<DownloadOutlined style={{ color: "#8c6cff" }} />}
            onClick={() =>
              gate.requirePaid(
                () => handleDownload(record.id, record.name),
                t("page.exportGateReason"),
              )
            }
          />
          <Popconfirm
            title={t("deleteConfirm.title")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("actions.delete")}
            cancelText={t("actions.cancel")}
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              icon={<DeleteOutlined style={{ color: "#8c6cff" }} />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="data-cleaning-page">
      <div className="data-cleaning-card">
        <div className="data-cleaning-card__header">
          <div>
            <Title level={4} className="data-cleaning-card__title">
              {t("page.title")}
            </Title>
            <Text className="data-cleaning-card__description">
              {t("page.description")}
            </Text>
          </div>
          <Button
            className="data-cleaning-btn data-cleaning-btn--primary"
            icon={<PlusOutlined />}
            onClick={handleOpenUpload}
          >
            {t("page.uploadNewDataset")}
          </Button>
        </div>

        <div className="data-cleaning-table-wrap">
          <GenericTable
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: false,
              onChange: (p) => {
                setPage(p);
                fetchRows(p);
              },
            }}
            emptyText={t("table.emptyText")}
            scroll={{ x: "max-content" }}
          />
        </div>
      </div>

      <UploadDatasetModal
        open={isUploadOpen}
        title={t("uploadModal.title")}
        uploadMeta={t("uploadModal.meta")}
        accept=".xlsx,.xls,.csv"
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        error={uploadError}
        submitting={uploading}
        onClose={() => setIsUploadOpen(false)}
        onSubmit={handleSubmitUpload}
      />
    </div>
  );
};

export default DataCleaningPage;
